/**
 * The Item screen's rules, as pure functions (ORDR-165).
 *
 * PICKS are kept per group, keyed by the group's PATH (`groupKey`), so a group
 * shared under two parents is two questions, as commerce asks it. A nested
 * group's picks are kept while its parent is not chosen and simply not read:
 * re-choosing the parent brings them back, and nothing hidden is ever sent or
 * validated.
 *
 * DEFAULTS START CHOSEN AND ARE SENT AS CHOSEN. The diner sees them ticked and
 * may untick them; what goes to the cart is exactly what is ticked. A SIZE IS
 * NEVER PRESELECTED (ORDR-108): a default size would be a price nobody picked.
 *
 * `problems` is courtesy, not the check. paio-commerce `models/selections.py`
 * validates the same tree again on every write and is the copy that protects
 * the order; this one tells the diner why before a round trip.
 *
 * `lineEstimate` is a DISPLAY estimate and is labelled as one where it is
 * drawn. The cart's figure is the server quote, always.
 */

import type { CatalogueChoice, CatalogueGroup, CatalogueItem } from "./catalogue.js";

/** Chosen choice ids, by group path, in the order they were chosen. */
export type Picks = Readonly<Record<string, readonly string[]>>;

/** One answer in paio-commerce's COMM-179 selection tree. Keys exactly as `ENTRY_KEYS`. */
export interface WireSelection {
  readonly group_id: string;
  readonly choice_id: string;
  readonly quantity: number;
  readonly children: readonly WireSelection[];
}

/** A root group's key is its id; a nested one's is the path through the choice that opens it. */
export function groupKey(parentKey: string | null, groupId: string, viaChoiceId?: string): string {
  return parentKey === null ? groupId : `${parentKey}>${viaChoiceId ?? ""}>${groupId}`;
}

/** "Choose 1", "Up to 3", "Choose 2 to 4". */
export function boundsLabel(group: CatalogueGroup): string {
  if (group.min === group.max) {
    return `Choose ${String(group.min)}`;
  }
  if (group.min === 0) {
    return `Up to ${String(group.max)}`;
  }
  return `Choose ${String(group.min)} to ${String(group.max)}`;
}

/** Whether a group takes exactly one answer, so a new pick replaces the old. */
export function takesOne(group: CatalogueGroup): boolean {
  return group.style === "SINGLE" || group.max === 1;
}

function seed(groups: readonly CatalogueGroup[], into: Record<string, string[]>, parentKey: string | null, via?: string): void {
  for (const group of groups) {
    const key = groupKey(parentKey, group.id, via);
    into[key] = group.choices.filter((choice) => choice.defaultSelected).map((choice) => choice.id);
    for (const choice of group.choices) {
      if (choice.child !== null) {
        seed([choice.child], into, key, choice.id);
      }
    }
  }
}

/** Every group's defaults, at every depth. */
export function initialPicks(groups: readonly CatalogueGroup[]): Picks {
  const picks: Record<string, string[]> = {};
  seed(groups, picks, null);
  return picks;
}

/** Turn one choice on or off. A pick past the group's maximum is refused (the picks come back unchanged). */
export function pick(picks: Picks, key: string, group: CatalogueGroup, choiceId: string, on: boolean): Picks {
  const current = picks[key] ?? [];
  if (!on) {
    return { ...picks, [key]: current.filter((id) => id !== choiceId) };
  }
  if (current.includes(choiceId)) {
    return picks;
  }
  if (takesOne(group)) {
    return { ...picks, [key]: [choiceId] };
  }
  return current.length >= group.max ? picks : { ...picks, [key]: [...current, choiceId] };
}

/** The delta of one choice at the chosen size; `null` when unknown or not priced at that size. */
export function choiceDelta(choice: CatalogueChoice, portionCode: string | null, portioned: boolean): number | null {
  if (!portioned) {
    return choice.priceDeltaMinor;
  }
  if (portionCode === null || choice.portionDeltas === null) {
    return null;
  }
  return choice.portionDeltas[portionCode] ?? null;
}

interface Visible {
  readonly group: CatalogueGroup;
  readonly chosen: readonly CatalogueChoice[];
}

/** Every group the diner can currently see, depth first, with what is chosen in it. */
function visibleGroups(groups: readonly CatalogueGroup[], picks: Picks, parentKey: string | null = null, via?: string): Visible[] {
  const out: Visible[] = [];
  for (const group of groups) {
    const key = groupKey(parentKey, group.id, via);
    const ids = picks[key] ?? [];
    const chosen = ids
      .map((id) => group.choices.find((choice) => choice.id === id))
      .filter((choice): choice is CatalogueChoice => choice !== undefined);
    out.push({ group, chosen });
    for (const choice of chosen) {
      if (choice.child !== null) {
        out.push(...visibleGroups([choice.child], picks, key, choice.id));
      }
    }
  }
  return out;
}

/** Why the line cannot be added yet, in the order the diner meets them; `[]` when it can. */
export function problems(item: CatalogueItem, portionCode: string | null, picks: Picks): string[] {
  const portioned = item.portions.length > 0;
  const portion = item.portions.find((entry) => entry.code === portionCode);
  const reasons: string[] = [];
  if (portioned && portion === undefined) {
    reasons.push("Choose a size");
  }
  for (const { group, chosen } of visibleGroups(item.groups, picks)) {
    if (chosen.length < group.min) {
      reasons.push(`Choose ${group.min === group.max ? "" : "at least "}${String(group.min)} in ${group.name}`);
    } else if (chosen.length > group.max) {
      reasons.push(`Choose no more than ${String(group.max)} in ${group.name}`);
    }
    if (portion !== undefined) {
      for (const choice of chosen) {
        if (choiceDelta(choice, portion.code, true) === null) {
          reasons.push(`${choice.name} is not available in ${portion.name}`);
        }
      }
    }
  }
  return reasons;
}

function treeFor(groups: readonly CatalogueGroup[], picks: Picks, parentKey: string | null, via?: string): WireSelection[] {
  const out: WireSelection[] = [];
  for (const group of groups) {
    const key = groupKey(parentKey, group.id, via);
    for (const id of picks[key] ?? []) {
      const choice = group.choices.find((entry) => entry.id === id);
      if (choice === undefined) {
        continue;
      }
      out.push({
        group_id: group.id,
        choice_id: choice.id,
        quantity: 1,
        children: choice.child === null ? [] : treeFor([choice.child], picks, key, choice.id),
      });
    }
  }
  return out;
}

/** The visible picks as the cart's selection tree, roots in the item's order. */
export function wireSelections(groups: readonly CatalogueGroup[], picks: Picks): WireSelection[] {
  return treeFor(groups, picks, null);
}

/**
 * A DISPLAY estimate of the line, in minor units: the size's price (or the unit
 * price) plus every visible chosen delta at that size, the cheapest charged
 * answers in each group freed up to its allowance, as commerce prices it.
 * `null` when any figure it needs is unknown.
 */
export function lineEstimate(item: CatalogueItem, portionCode: string | null, picks: Picks, quantity: number): number | null {
  const portioned = item.portions.length > 0;
  const base = portioned ? item.portions.find((entry) => entry.code === portionCode)?.amountMinor : item.unitAmountMinor;
  if (base === undefined || base === null) {
    return null;
  }
  let total = base;
  for (const { group, chosen } of visibleGroups(item.groups, picks)) {
    const deltas = chosen.map((choice) => choiceDelta(choice, portionCode, portioned));
    if (deltas.some((delta) => delta === null)) {
      return null;
    }
    const known = deltas as number[];
    const charged = known.filter((delta) => delta > 0).sort((a, b) => a - b);
    total += charged.slice(group.freeAllowance).reduce((sum, delta) => sum + delta, 0);
    total += known.filter((delta) => delta <= 0).reduce((sum, delta) => sum + delta, 0);
  }
  return total * quantity;
}
